/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-prototype-builtins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { getValueDefault } from '@linc-technologies/linced-common/utils/dynamic-field-types';
import { canHashContact } from 'linced-app/utils/fields';
import { serializeQueryParams } from 'linced-app/utils/url';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import ApplicationStatus from 'linced-app/enums/application-status';
import ContactType from 'linced-app/enums/contact-type';
import ContactRelationship from '@linc-technologies/linced-common/enums/contact-relationship';
import MedicalRelationship from '@linc-technologies/linced-common/enums/medical-relationship';
import MedicalConditionType from '@linc-technologies/linced-common/enums/medical-condition-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import config from 'linced-app/config/environment';
import {
  compact,
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class ViewApplicationController extends Controller {
  @controller('application')
  appController;

  // Services
  @service
  fetch;

  @service
  paperToaster;

  @service
  router;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @computed(
    'schema.{sections.[],primaryContacts,emergencyContacts,medicalContacts,medicalConditions}',
  )
  get allSections() {
    let sections = this.schema.sections.slice();
    sections.push(
      ...compact([
        this.schema.primaryContacts,
        this.schema.emergencyContacts,
        this.schema.medicalContacts,
        this.schema.medicalConditions,
      ]),
    );
    return sections;
  }

  @computed('schema.medicalConditions.fields.@each.key')
  get subtypeField() {
    if (isEmpty(this.schema.medicalConditions)) {
      return null;
    }
    return findBy(
      this.get('schema.medicalConditions.fields'),
      'key',
      'core:subtype',
    );
  }

  @computed('schema.medicalConditions.fields.@each.key')
  get epiPenField() {
    if (isEmpty(this.schema.medicalConditions)) {
      return null;
    }
    return findBy(
      this.get('schema.medicalConditions.fields'),
      'key',
      'core:autoinjectorHeldOnsite',
    );
  }

  @computed('model.caregiverFieldGroups.@each.key')
  get relationshipFields() {
    let group = this.model.caregiverFieldGroups.find(
      (item) => item.key === 'relationship',
    );
    if (isEmpty(group)) {
      return [];
    }
    return group.keyedFields;
  }

  @computed('model.caregiverFieldGroups.[]', 'relationshipFields.[]')
  get caregiverFields() {
    let fields = [];
    let groups = this.model.caregiverFieldGroups.filter(
      (item) => item.key !== 'relationship',
    );
    groups.forEach((group) => {
      let groupFields = group.keyedFields;
      if (groupFields.length > 0) {
        groupFields.forEach((field) => fields.push(field));
      }
    });
    return fields;
  }

  @computed('model.medicalFieldGroups.@each.key')
  get medicalRelationshipFields() {
    let group = this.model.medicalFieldGroups.find(
      (item) => item.key === 'relationship',
    );
    if (isEmpty(group)) {
      return [];
    }
    return group.keyedFields;
  }

  @computed('model.medicalFieldGroups.[]', 'medicalRelationshipFields.[]')
  get medicalFields() {
    let fields = [];
    let groups = this.model.medicalFieldGroups.filter(
      (item) => item.key !== 'relationship',
    );
    groups.forEach((group) => {
      let groupFields = group.keyedFields;
      if (groupFields.length > 0) {
        groupFields.forEach((field) => fields.push(field));
      }
    });
    return fields;
  }

  ApplicationStatus = ApplicationStatus;
  ContactType = ContactType;
  ContactRelationship = ContactRelationship;
  MedicalRelationship = MedicalRelationship;
  MedicalConditionType = MedicalConditionType;
  Jurisdiction = Jurisdiction;
  @tracked
  learner = null;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  processDialogOpen = false;
  @tracked
  contactMapperOpen = false;
  @tracked
  contactMappingSkipped = false;

  @tracked
  contacts;

  @tracked
  medicalContacts;

  @computed('model.application.status')
  get isProcessed() {
    return (
      this.get('model.application.status') === ApplicationStatus.DECLINED ||
      this.get('model.application.status') === ApplicationStatus.ACCEPTED
    );
  }

  @computed('contacts.@each.processing', 'medicalContacts.@each.processing')
  get contactsProcessing() {
    return (
      this.contacts.some((contact) => contact.processing) ||
      this.medicalContacts.some((contact) => contact.processing)
    );
  }

  @action
  print() {
    window.print();
  }

  @action
  decline() {
    let record = this.get('model.application');
    record.set('status', ApplicationStatus.DECLINED);
    record.save().then(() => {
      this.appController.refreshUnprocessedApplications();
      this.router.transitionTo('enrolment.applications');
    });
  }

  @action
  accept() {
    this.contactMappingSkipped = false;
    this.processDialogOpen = true;
  }

  @action
  closeProcessDialog() {
    this.processDialogOpen = false;
  }

  // finishProcessing provides the callback for when processing an
  // application has been completed.
  @action
  finishProcessing() {
    let record = this.get('model.application');
    // Process medicalConditions if they exist
    if (this.model.application.get('medicalConditions').length) {
      this.store.findRecord('person', record.personId).then((person) => {
        this.processMedicalConditions(person);
      });
    }
    this.paperToaster.show(
      `${record.get('firstName')} ${record.get('lastName')} is now enrolled.`,
      {
        duration: 4000,
        position: 'top right',
        toastClass: 'success',
      },
    );

    this.appController.refreshUnprocessedApplications();
  }

  @action
  cancelProcessing() {
    this.processDialogOpen = false;
  }

  @action
  openContactMapper() {
    this.contactMapperOpen = true;
  }

  @action
  closeContactMapper() {
    this.contactMapperOpen = false;
  }

  @action
  processContacts(openContactMapper) {
    let application = this.model.application;
    let contacts = [];
    let medicalContacts = [];
    let numPrimary = 0;
    let numEmergency = 0;
    let numMedical = 0;
    if (!isEmpty(this.schema.primaryContacts)) {
      numPrimary = this.schema.primaryContacts.count;
    }
    if (!isEmpty(this.schema.emergencyContacts)) {
      numEmergency = this.schema.emergencyContacts.count;
    }
    if (!isEmpty(this.schema.medicalContacts)) {
      numMedical = this.schema.medicalContacts.count;
    }

    if (isEmpty(application.personId)) {
      // We don't seem to have a personId for the learner.
      // Don't try mapping contacts, or they may end up being put on the wrong person.
      this.contacts = contacts;
      this.contactMappingSkipped = true;
      if (openContactMapper) {
        // Open dialog if requested
        this.contactMapperOpen = true;
      }
      return false;
    }

    let primaryContacts = application.contacts.filter(
      (item) => item.type === ContactType.PRIMARY,
    );
    let emergencyContacts = application.contacts.filter(
      (item) => item.type === ContactType.EMERGENCY,
    );
    for (let i = 0; i < numPrimary; i++) {
      let contact = primaryContacts[i];
      if (!isEmpty(contact) && this.isContactPresent(contact)) {
        // Contact specified.  Add them to the list.
        contacts.push({
          data: contact,
          priority: i + 1,
          processing: true,
          result: null,
          person: null,
          complete: false,
        });
      }
    }
    for (let i = 0; i < numEmergency; i++) {
      let contact = emergencyContacts[i];
      if (!isEmpty(contact) && this.isContactPresent(contact)) {
        // Contact specified.  Add them to the list.
        contacts.push({
          data: contact,
          priority: i + 1,
          processing: true,
          result: null,
          person: null,
          complete: false,
        });
      }
    }
    // We don't seem to have a personId for the learner.
    // Don't try mapping contacts, or they may end up being put on the wrong person.
    this.contacts = contacts;

    for (let i = 0; i < numMedical; i++) {
      let contact = application.medicalContacts.objectAt(i);
      if (!isEmpty(contact) && this.isContactPresent(contact)) {
        // Contact specified.  Add them to the list.
        medicalContacts.push({
          data: contact,
          priority: i + 1,
          processing: true,
          results: null,
          person: null,
          complete: false,
        });
      }
    }
    this.medicalContacts = medicalContacts;

    // If we have any contacts to process, open the dialog.
    if (contacts.length > 0 || medicalContacts.length > 0) {
      if (openContactMapper) {
        // Open dialog if requested
        this.contactMapperOpen = true;
      }
      this.store
        .findRecord('person', this.model.application.personId)
        .then((person) => {
          this.learner = person;
          this.processApplicationContacts(this.model.application, person);
          this.processMedicalContacts(person);
        });
    }

    return { contacts, medicalContacts };
  }

  processMedicalConditions(person) {
    return this.schoolSchema.getPeopleSchema().then((schema) => {
      let priorityCount = 1;
      this.model.application.get('medicalConditions').forEach((condition) => {
        if (
          isEmpty(condition.get('type')) &&
          isEmpty(condition.get('description')) &&
          isEmpty(condition.fields.get('core:subtype.value'))
        ) {
          // Skip this iteration, insufficient info to create medical condition
          return;
        }

        let haveSubtypeNoType =
          isEmpty(condition.get('type')) &&
          !isEmpty(condition.fields.get('core:subtype.value'));
        let haveTypeNoSubtype =
          !isEmpty(condition.get('type')) &&
          isEmpty(condition.fields.get('core:subtype.value'));
        let haveOnlyDescription =
          !isEmpty(condition.get('description')) &&
          isEmpty(condition.fields.get('core:subtype.value')) &&
          isEmpty(condition.get('type'));

        switch (true) {
          case haveSubtypeNoType:
            if (
              condition.fields.get('core:subtype.value').includes('Allergy')
            ) {
              condition.set('type', MedicalConditionType.NON_SERIOUS_ALLERGY);
            } else {
              condition.set(
                'type',
                MedicalConditionType.NON_SERIOUS_MEDICAL_CONDITION,
              );
            }
            break;
          case haveTypeNoSubtype:
            if (
              condition.get('type') === MedicalConditionType.SERIOUS_ALLERGY ||
              condition.get('type') === MedicalConditionType.NON_SERIOUS_ALLERGY
            ) {
              condition
                .get('fields.core:subtype')
                .set('value', 'Allergy - Other');
            } else {
              condition
                .get('fields.core:subtype')
                .set('value', 'Other Health Condition');
            }
            break;
          case haveOnlyDescription:
            condition
              .get('fields.core:subtype')
              .set('value', 'Other Health Condition');
            condition.set(
              'type',
              MedicalConditionType.NON_SERIOUS_MEDICAL_CONDITION,
            );
            break;
        }
        let conditionFragment = this.store.createFragment(
          'people/medical-condition',
          {
            type: condition.get('type'),
            description: condition.get('description'),
            priority: priorityCount,
            highRisk: condition.get('highRisk'),
            anaphylaxis: condition.get('anaphylaxis'),
            prescribedMedicationHeld: condition.get('prescribedMedicationHeld'),
            startDate: condition.get('startDate'),
            reviewDate: condition.get('reviewDate'),
            endDate: condition.get('endDate'),
          },
        );
        person.get('learner.medicalConditions').addFragment(conditionFragment);
        priorityCount++;
        this.populateMedicalConditionFields(
          schema,
          conditionFragment,
          condition.fields,
        );
      });
      return person.save();
    });
  }

  populateContactFields(contact, data) {
    this.relationshipFields.forEach((field) => {
      if (data.hasOwnProperty(field.key)) {
        let contactField = contact.get(`fields.${field.key}`);
        if (isEmpty(contactField)) {
          contact.addField(field.key, field.dataType);
          contactField = contact.get(`fields.${field.key}`);
        }
        contactField.set('value', data.get(`${field.key}.value`));
      }
    });
  }

  findMedicalContacts(contact) {
    let names = [
      contact.firstName,
      contact.lastName,
      contact.get('fields.core:medicalCentre.valueString'),
    ];
    let params = {
      query: compact(names).join(' '),
      schoolId: this.get('session.activeSchool'),
    };

    return this.fetch.request(
      `${config.APP.apiHost}/people/v4/medical/:search?${serializeQueryParams(
        params,
      )}`,
    );
  }

  findContact(query) {
    let baseUrl = `${config.APP.apiHost}/people/v4/caregivers/:search?`;
    let hashQuery = '';
    if (query.isHashable) {
      let hashParams = {
        first: query.first,
        last: query.last,
        home: query.home,
        mobile: query.mobile,
        jurisdiction: this.jurisdiction,
      };
      hashQuery = serializeQueryParams(hashParams);
    }
    if (!isEmpty(query.email)) {
      // emailQuery includes first and last names for fuzzy name matching
      let emailQuery = serializeQueryParams({
        query: query.email.toLowerCase().trim(),
        first: query.first,
        last: query.last,
      });
      return this.fetch.request(`${baseUrl}${emailQuery}`).then((results) => {
        if (!!results && !!results.caregivers && results.caregivers.length) {
          // We have results.
          let result = results.caregivers.find((caregiver) => {
            return caregiver.fuzzyNameMatch;
          });
          if (result) {
            // We have a result which matches the email address and fuzzy-matches the names.
            if (result.inThisSchool) {
              // They're already associated with this school; return them now.
              return {
                caregivers: [result],
              };
            } else {
              // They're not associated with this school.
              // See if we can link them.
              return this.enableCaregiverTenantAccess(result.personId)
                .then(() => {
                  // Successfully linked.
                  result.inThisSchool = true;
                  return {
                    caregivers: [result],
                  };
                })
                .catch(() => {
                  // Linking failed (probably not connected elsewhere).
                  return {
                    inThisSchool: false,
                  };
                });
            }
          } else {
            // We have a match for email address, but not for name.
            // Return no results, so that a new caregiver will be generated
            // (without email address) - but with a bool to flag where the
            // conflicting user is (in this school or not).
            let inThisSchool =
              get(results, 'caregivers.0.inThisSchool') || false;
            return { inThisSchool };
          }
        } else if (!query.isHashable) {
          return results;
        }
        return this.fetch.request(`${baseUrl}${hashQuery}`);
      });
    } else {
      return this.fetch.request(`${baseUrl}${hashQuery}`);
    }
  }

  enableCaregiverTenantAccess(caregiverId) {
    let url = `${config.APP.apiHost}/people/v4/caregivers/${caregiverId}/:enableTenantAccess`;
    let data = {
      personId: caregiverId,
      schoolId: this.get('session.activeSchool'),
      learnerId: this.get('learner.id'),
    };
    let options = {
      data: JSON.stringify(data),
    };
    return this.fetch.post(url, options);
  }

  processMedicalContacts(person) {
    return this.schoolSchema
      .getPeopleSchema()
      .then((peopleSchema) => {
        const medicalContactsPromises = this.medicalContacts.map((contact) => {
          return this.findMedicalContacts(contact.data).then((resp) => {
            if (!isEmpty(resp.medicalContacts)) {
              // We have at least one existing medical contact which matches the
              // provided name(s).  This may or may not be a proper match, as we
              // may not have had all name fields supplied.
              set(contact, 'results', resp.medicalContacts);
              let peoplePromises = contact.results.map((result) =>
                this.store
                  .findRecord('person', result.personId)
                  .then((person) => set(result, 'person', person)),
              );
              return Promise.all(peoplePromises).finally(() => {
                set(contact, 'processing', false);
              });
            } else {
              // Contact doesn't exist. create one and add it to the learner
              return this.createMedicalContact(contact, peopleSchema);
            }
          });
        });

        // Use Promise.all to wait for all promises to resolve
        return Promise.all(medicalContactsPromises);
      })
      .then(() => {
        // Push in any generated contact fragments.
        this.medicalContacts.forEach((contact) => {
          if (!isEmpty(contact.fragment)) {
            person.learner.medicalContacts.pushObject(contact.fragment);
          }
        });
        if (person.get('hasDirtyAttributes')) {
          person.save();
        }
      });
  }

  processApplicationContacts(application, person) {
    return this.schoolSchema.getPeopleSchema().then((peopleSchema) => {
      let promise = this.contacts.reduce((promise, contact) => {
        if (isEmpty(contact.data.caregiverId)) {
          // We don't already have a Person ID for this contact - search for and/or create them.
          let email = contact.data.get('fields.core:emailAddress.value');
          let params = {
            first: contact.data.get('fields.core:firstName.value') ?? '',
            last: contact.data.get('fields.core:lastName.value') ?? '',
            home: contact.data.get('fields.core:phoneHome.value') ?? '',
            mobile: contact.data.get('fields.core:phoneMobile.value') ?? '',
          };
          let hashable = canHashContact(
            params.first,
            params.last,
            params.home,
            params.mobile,
          );
          let hasEmail = !isEmpty(email);

          if (hashable || hasEmail) {
            // We have an email address and/or a set of hashable data to search for.
            // Flag whether we have enough data to generate a contactHash, so it can be used as a fallback.
            params.isHashable = hashable;

            // If we have an email address, provide that so that we can perform an initial search by email address.
            if (hasEmail) {
              // Conform email address to lowercase and trim off whitespace.
              email = email.toLowerCase().trim();
              // Push cleaned email address back into the field.
              contact.data.set('fields.core:emailAddress.value', email);
              // Add email address to the search params.
              params.email = email;
            }

            promise = promise.then(() => {
              return this.findContact(params).then((resp) => {
                if (!isEmpty(resp) && !isEmpty(resp.caregivers)) {
                  // Searching should return at most one match; return the first result here.
                  return resp.caregivers[0];
                } else if (resp.hasOwnProperty('inThisSchool')) {
                  // We got a conflict; flag the contact with the location.
                  set(contact, 'conflictInThisSchool', resp.inThisSchool);
                  set(contact, 'skippedEmail', email);

                  if (hashable) {
                    // If we have enough non-email data to generate a contact
                    // hash, try searching that way to avoid conflicts.
                    params.email = '';
                    contact.data
                      .get('fields.core:emailAddress')
                      .set('value', '');
                    return this.findContact(params).then((resp) => {
                      if (!isEmpty(resp) && !isEmpty(resp.caregivers)) {
                        // Return the first result, if we got any.
                        return resp.caregivers[0];
                      }
                      // Don't try anything more - there should be no conflict.
                      return null;
                    });
                  }
                }
                return null;
              });
            });
          }
        }

        return promise.then((existing) => {
          let contacts = person.get('learner.contacts');
          if (isEmpty(existing)) {
            // No match found.  Create a new Caregiver and associate them with the Learner.
            let caregiver = this.schoolSchema.newPerson(PersonType.CAREGIVER);
            this.populateFields(
              peopleSchema,
              PersonType.CAREGIVER,
              caregiver,
              contact.data.fields,
              null,
            );
            return caregiver
              .save()
              .catch((err) => {
                let detail = get(err, 'errors.0.detail');
                if (detail === 'Resource already exists') {
                  // Email address already exists on another contact - likely a shared "mum & dad" email address.
                  // Remove the email address and try again.
                  let field = caregiver.get('fields.core:emailAddress');
                  set(contact, 'skippedEmail', field.get('value'));
                  field.set('value', '');
                  return caregiver.save();
                }
                // This wasn't a conflict; mark failed and re-reject
                // so we don't create a contact with no caregiver.
                set(contact, 'failed', true);
                return Promise.reject(err);
              })
              .then((c) => {
                let fragment = this.store.createFragment('people/contact', {
                  type: contact.data.type,
                  caregiverId: c.id,
                  relationship: contact.data.relationship,
                  notes: contact.data.notes,
                  billPayer: contact.data.billPayer,
                  priority: contact.priority,
                });
                this.populateContactFields(fragment, contact.data.fields);
                set(contact, 'processing', false);
                set(contact, 'complete', true);
                set(contact, 'fragment', fragment);
              });
          } else if (existing.inThisSchool) {
            // We found an existing Caregiver, who is already associated with this School.
            if (
              isEmpty(
                findBy(
                  filterBy(contacts, 'type', contact.data.type),
                  'caregiverId',
                  existing.personId,
                ),
              )
            ) {
              // They're not yet associated with this Learner.  Go ahead and add them.
              return this.store
                .findRecord('person', existing.personId)
                .then((caregiver) => {
                  let saveCaregiver = false;
                  // Remove the email address entirely from COMPARE DETAILS.
                  // We need to ALWAYS retain the email address if it exists.
                  let contactEmail = contact.data.get(
                    'fields.core:emailAddress.value',
                  );
                  let caregiverEmail = caregiver.get(
                    'fields.core:emailAddress.value',
                  );
                  if (contactEmail === '' && caregiverEmail !== '') {
                    // Sync value in contact and caregiver to sidestep the contact mapper process
                    contact.data.set(
                      'fields.core:emailAddress.value',
                      caregiverEmail,
                    );
                  }
                  if (caregiverEmail === '' && contactEmail !== '') {
                    // Save the new value to the existing caregiver
                    caregiver.set(
                      'fields.core:emailAddress.value',
                      contactEmail,
                    );
                    saveCaregiver = true;
                  }

                  // Remove the mobile phone number from COMPARE DETAILS if either the incoming or the existing is blank.
                  // We need to ALWAYS use a mobile phone number if it exists on either record.
                  // It will only appear in COMPARE DETAILS if we have matched by email address and the mobile is different.
                  let contactMobile = contact.data.get(
                    'fields.core:phoneMobile.value',
                  );
                  let caregiverMobile = caregiver.get(
                    'fields.core:phoneMobile.value',
                  );
                  if (contactMobile === '' && caregiverMobile !== '') {
                    // Sync value in contact and caregiver to sidestep the contact mapper process
                    contact.data.set(
                      'fields.core:phoneMobile.value',
                      caregiverMobile,
                    );
                  }
                  if (caregiverMobile === '' && contactMobile !== '') {
                    // Save the new value to the existing caregiver
                    caregiver.set(
                      'fields.core:phoneMobile.value',
                      contactMobile,
                    );
                    saveCaregiver = true;
                  }

                  if (this.isContactIdentical(contact.data, caregiver)) {
                    // Supplied details match those already on file - just add them as a contact.
                    let fragment = this.store.createFragment('people/contact', {
                      type: contact.data.type,
                      caregiverId: existing.personId,
                      relationship: contact.data.relationship,
                      notes: contact.data.notes,
                      billPayer: contact.data.billPayer,
                      priority: contact.priority,
                    });
                    this.populateContactFields(fragment, contact.data.fields);
                    set(contact, 'complete', true);
                    set(contact, 'fragment', fragment);
                    if (saveCaregiver) {
                      caregiver.save();
                    }
                  }

                  // If there are differences, we won't have marked them as "complete", so this will offer a button
                  // to review differences and take appropriate action.
                  set(contact, 'result', existing);
                  set(contact, 'person', caregiver);
                  set(contact, 'processing', false);
                  if (saveCaregiver) {
                    set(contact, 'isDirty', true);
                  }
                });
            } else {
              // They're already associated - we're done here.
              set(contact, 'processing', false);
              set(contact, 'complete', true);
            }
          }
        });
      }, Promise.resolve());

      promise.then(() => {
        // Push in any generated contact fragments.
        this.contacts.forEach((contact) => {
          if (!isEmpty(contact.fragment)) {
            person.learner.contacts.pushObject(contact.fragment);
          }
        });
        if (person.get('hasDirtyAttributes')) {
          person.save();
        }
      });
    });
  }

  isContactPresent(contact) {
    let section = '';
    if (contact.type === ContactType.PRIMARY) {
      section = 'primaryContacts';
    } else if (contact.type === ContactType.EMERGENCY) {
      section = 'emergencyContacts';
    } else {
      section = 'medicalContacts';
    }
    let fields = this.schema.get(`${section}.keyedFields`);
    return !fields.every((field) => {
      let contactValue = contact.get(`fields.${field.key}.value`);
      let defaultValue = getValueDefault(field.dataType);
      if (isEmpty(field.default)) {
        // No default value; just check for emptiness or default for the datatype.
        return isEmpty(contactValue) || contactValue === defaultValue;
      } else {
        // Default value; check against default as well as for emptiness or default for the datatype.
        return (
          contactValue === field.get('default.value') ||
          isEmpty(contactValue) ||
          contactValue === defaultValue
        );
      }
    });
  }

  isContactIdentical(contact, person) {
    let section =
      contact.type === ContactType.PRIMARY
        ? 'primaryContacts'
        : 'emergencyContacts';
    let fields = this.schema.get(`${section}.keyedFields`);
    return fields.every((field) => {
      let relationshipField = this.relationshipFields.find(
        (item) => item.key === field.key,
      );
      if (!isEmpty(relationshipField)) {
        // This field relates to the relationship between caregiver and learner - don't try to compare it.
        return true;
      }
      let contactValue = contact.get(`fields.${field.key}.value`) ?? '';
      let personValue = person.get(`fields.${field.key}.value`) ?? '';
      return (
        contactValue.toString().toLowerCase().trim() ===
        personValue.toString().toLowerCase().trim()
      );
    });
  }

  @action
  createEnrolment(application, startDate) {
    let appFields = application.get('fields');

    return this.schoolSchema.getPeopleSchema().then((schema) => {
      // Create Enrolment record and Learner subrecord
      let enrolment = this.store.createRecord('people/enrolment', {
        applicationId: application, // applicationId is a belongsTo relationship
        personId: application.personId,
        jurisdiction: this.jurisdiction,
        jurisdictionalId: application.jurisdictionalId,
        status: EnrolmentStatus.PRE_ENROLLED,
        entryDate: startDate,
        fieldsArray: [],
      });
      enrolment.set('learner', {
        schoolId: this.session.activeSchool,
      });
      // TODO: Add Contacts to the Learner subrecord, once we've added them to Applications.

      this.populateFields(
        schema,
        PersonType.LEARNER,
        enrolment,
        appFields,
        application.jurisdictionalId,
      );

      return enrolment;
    });
  }

  populateFields(schema, personType, entity, appFields, jurisdictionalId) {
    let groups = schema.filter((item) => item.personType === personType);

    groups.forEach((group) => {
      if (
        (personType === PersonType.CAREGIVER ||
          personType === PersonType.DOCTORS_AND_SPECIALISTS) &&
        group.key === 'relationship'
      ) {
        // Don't push relationship fields into the Person record.
        return;
      }
      group.keyedFields.forEach((field) => {
        // Fetch field from Application, if present
        let appField = appFields.get(field.key);

        // Add field to the entity
        entity.addField(field.key, field.dataType);

        if (field.inputType === 14) {
          // InputType_JurisdictionalId = 14
          // This is the jurisdictional identifier; pull it from the application record.
          entity.fields.set(`${field.key}.value`, jurisdictionalId);
        } else if (!isEmpty(appField)) {
          // This field was included in the Application; push its value into the entity.
          if (field.dataType === 2) {
            // DataType_LIST = 2
            // Value is a list, so needs to be copied rather than just mushed in.
            // Copy `valueList` rather than `value` to prevent issues with fields whose types have changed.
            let valueList = appField.get('valueList');
            if (!isEmpty(valueList)) {
              entity.fields.set(`${field.key}.value`, valueList.copy());
            }
          } else {
            entity.fields.set(`${field.key}.value`, appField.get('value'));
          }
        } else if (!isEmpty(field.default)) {
          // This field wasn't in the Application, but the field has a default value.  Use that.
          if (field.dataType === 2) {
            // DataType_LIST = 2
            // Value is a list, so needs to be copied rather than just mushed in.
            // Copy `valueList` rather than `value` to prevent issues with fields whose types have changed.
            if (!isEmpty(field.default.valueList)) {
              entity.fields.set(
                `${field.key}.value`,
                field.default.valueList.copy(),
              );
            }
          } else {
            entity.fields.set(`${field.key}.value`, field.default.value);
          }
        }
      });
    });
  }

  populateMedicalConditionFields(schema, entity, appFields) {
    this.schema.medicalConditions.keyedFields.forEach((field) => {
      // Fetch field from Application, if present
      let appField = appFields.get(field.key);

      // Add field to the entity
      entity.addField(field.key, field.dataType);

      if (!isEmpty(appField)) {
        if (field.dataType === 2) {
          let valueList = appField.get('valueList');
          if (!isEmpty(valueList)) {
            entity.fields.set(`${field.key}.value`, valueList.copy());
          }
        } else {
          entity.fields.set(`${field.key}.value`, appField.get('value'));
        }
      } else if (!isEmpty(field.default)) {
        entity.fields.set(`${field.key}.value`, field.default.value);
      }
    });
  }

  @action
  createMedicalContact(contact, peopleSchema) {
    let medicalContact = this.schoolSchema.newPerson(
      PersonType.DOCTORS_AND_SPECIALISTS,
    );
    this.populateFields(
      peopleSchema,
      PersonType.DOCTORS_AND_SPECIALISTS,
      medicalContact,
      contact.data.fields,
      null,
    );

    return medicalContact.save().then((m) => {
      let fragment = this.store.createFragment('people/medical-contact', {
        medicalContactId: m.id,
        relationship: contact.data.medicalRelationship,
        notes: contact.data.notes,
        permissionToContact: contact.data.permissionToContact,
        priority: contact.priority,
      });
      this.populateContactFields(fragment, contact.data.fields);
      set(contact, 'processing', false);
      set(contact, 'complete', true);
      set(contact, 'fragment', fragment);
    });
  }

  saveEnrolment(enrolment) {
    let record = this.get('model.application');
    // Save the new record and (on success) redirect to their info page
    enrolment.save().then((saved) => {
      let personId = saved.get('personId');
      record.set('status', ApplicationStatus.ACCEPTED);
      record.set('personId', personId);
      record
        .save()
        .then(() => {
          this.paperToaster.show(
            `${record.get('firstName')} ${record.get(
              'lastName',
            )} is now pre-enrolled.`,
            {
              duration: 4000,
              position: 'top right',
              toastClass: 'success',
            },
          );

          this.appController.refreshUnprocessedApplications();
          this.router.transitionTo('people.person.information', personId);
        })
        .catch(() => {
          this.paperToaster.show(
            `Failed to pre-enrol ${record.get('firstName')} ${record.get(
              'lastName',
            )}.`,
            {
              duration: 4000,
              position: 'top right',
              toastClass: 'warning',
            },
          );
        });
    });
  }

  reset() {
    this.learner = null;
    this.processDialogOpen = false;
    this.contactMapperOpen = false;
    this.contactMappingSkipped = false;
  }
}
